import React, { useContext, useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Calendar } from "../ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { CalendarIcon, Download, Filter, Share, X } from "lucide-react";
import {
  format,
  startOfDay,
  endOfDay,
  subDays,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
} from "date-fns";
import { AppContext } from '../../src/App';
import { toast } from "sonner";

import { apiClient } from "@/Utils/apiClient";
import { buildQuery, flattenFilters } from "@/Utils/helpers";
import { useAuth } from "../AuthContext";
import { getLastWeekRange } from "@/Utils/helpers";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Checkbox } from "../ui/checkbox";

export interface DashboardFilters {
  dateRange: { from: Date; to: Date };
  program: string;
  batchType: string;
  status: string;
  searchTerm: string;
  batchStatus: string;
  auditorView: boolean;
}

interface DashboardFiltersProps {
  filters: DashboardFilters;
  onFiltersChange: (filters: DashboardFilters) => void;
}

export function DashboardFiltersComponent({
  filters,
  onFiltersChange,
}: DashboardFiltersProps) {

  const { sideBarItems } = useContext(AppContext);
  const dashBoardMenu = sideBarItems.find((item: any) => item.name === "Dashboard");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempRange, setTempRange] = useState({
    from: filters.dateRange.from,
    to: filters.dateRange.to,
  });
  const { token } = useAuth();


  const quickDateRanges = [
    {
      label: "Today",
      value: {
        from: startOfDay(new Date()),
        to: endOfDay(new Date()),
      },
    },
    {
      label: "Last Week",
      value: getLastWeekRange()
    },
    {
      label: "Last 7 days",
      value: {
        from: startOfDay(subDays(new Date(), 6)),
        to: endOfDay(new Date()),
      },
    },
    {
      label: "Last 30 days",
      value: {
        from: startOfDay(subDays(new Date(), 29)),
        to: endOfDay(new Date()),
      },
    },
    {
      label: "This month",
      value: {
        from: startOfMonth(new Date()),
        to: endOfMonth(new Date()),
      },
    },
    {
      label: "Last month",
      value: {
        from: startOfMonth(
          subDays(startOfMonth(new Date()), 1),
        ),
        to: endOfMonth(subDays(startOfMonth(new Date()), 1)),
      },
    },
    {
      label: "This year",
      value: {
        from: startOfYear(new Date()),
        to: endOfDay(new Date()),
      },
    },
  ];

  const handleQuickDateRange = (range: {
    from: Date;
    to: Date;
  }) => {
    onFiltersChange({
      ...filters,
      dateRange: range,
    });
  };

  const handleFilterChange = (
    key: keyof DashboardFilters,
    value: any,
  ) => {
    onFiltersChange({
      ...filters,
      [key]: value,
    });
  };

  const clearFilter = (key: keyof DashboardFilters) => {
    if (key === "dateRange") {
      onFiltersChange({
        ...filters,
        dateRange: quickDateRanges[2]?.value || { from: new Date(), to: new Date() }, // Default to last 30 days
      });
    } else if (key === "batchType") {
      onFiltersChange({
        ...filters,
        [key]: "real-run",
      });
    } else {
      onFiltersChange({
        ...filters,
        [key]: "all",
      });
    }
  };

  const clearAllFilters = () => {
    onFiltersChange({
      dateRange: quickDateRanges[2]?.value ?? { from: new Date(), to: new Date() }, // Default to last 30 days
      program: "all",
      batchType: "real-run",
      status: "all",
      searchTerm: "",
      batchStatus: "",
      auditorView: false,
    });
  };

  const activeFilterCount = [
    filters.program !== "all",
    filters.batchType !== "real-run",
    filters.status !== "all",
    filters.auditorView === true,
  ].filter(Boolean).length;
  const downloadDashboardData = async () => {
    try {
      const flat = flattenFilters(filters);
      const preprocessFilters = (filters: any) => {
        const copy = { ...filters };

        if (copy.program === "all") copy.program = null;
        if (copy.status === "all") copy.status = null;
        if (copy.batchType === "real-run") copy.batchType = null;
        if (copy.auditorView) {
          copy.auditorView = "auditor";
        } else {
          copy.auditorView = null;
        }

        return copy;
      };
      const cleaned = preprocessFilters(flat);
      const query = buildQuery(cleaned, {
        from: "fromDate",
        to: "toDate",
        program: "chartType",
        batchType: "batchType",
        status: "decisionType",
        searchTerm: "search",
        batchStatus: "batchStatus",
        auditorView: "view",
      });
      const response = await apiClient(`dashboard/download?${query}`, {
        method: 'GET',
        token
      })
      // const result=await response.json();
      // if(!result.success){
      //   toast.error(result.message)
      //   throw new Error(result.message)
      // }
      // const zipBlob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });

      const blob = await response.blob();

      const contentDisposition = response.headers.get("content-disposition");
      let filename = `Dashboard-Data_${format(new Date(), "yyyy-MM-dd")}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }
    catch (e: any) {
      console.log(e)
      toast.error(e?.message)

    }
  }
  return (
    <div className="mb-3">
      {/* Ultra Compact Inline Filters */}
      <div className="flex items-center gap-6 py-2 px-3 bg-gray-50/80 rounded-lg border border-gray-200/60">
        {/* Date Range Filter */}
        <div className="flex items-center gap-1">
          <CalendarIcon className="w-4 h-4 text-gray-500 " />
          <Select
            value={
              quickDateRanges.find(
                (range) =>
                  range?.value?.from.getTime() ===
                  filters.dateRange.from.getTime() &&
                  range?.value?.to.getTime() ===
                  filters.dateRange.to.getTime(),
              )?.label || "custom"
            }
            onValueChange={(value) => {
              if (value === "custom") {
                setShowDatePicker(true);
                //              setTimeout(() => {
                //   setShowDatePicker(true);
                // }, 50);
              } else {
                const range = quickDateRanges.find(
                  (r) => r.label === value,
                );
                if (range) {
                  handleQuickDateRange(range.value);
                }
              }
            }}
            onOpenChange={(open) => {
              // when closing and "custom" is already selected, reopen the picker
              if (!open) {
                const selected =
                  quickDateRanges.find(
                    (range) =>
                      range?.value?.from.getTime() === filters.dateRange.from.getTime() &&
                      range?.value?.to.getTime() === filters.dateRange.to.getTime(),
                  )?.label || "custom";

                if (selected === "custom") {
                  setShowDatePicker(true);
                }
              }
            }}
          >
            <SelectTrigger className="w-24 h-6 text-sm border-0 bg-white/60 hover:bg-white w-auto">
              <SelectValue placeholder="Range" />
            </SelectTrigger>
            <SelectContent>
              {quickDateRanges.map((range) => (
                <SelectItem
                  key={range?.label}
                  value={range?.label}
                >
                  {range?.label}
                </SelectItem>
              ))}
              <SelectItem value="custom">
                <div className="flex items-center gap-2">
                  {/* <CalendarIcon className="w-3 h-3" /> */}
                  Custom
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-gray-600 font-medium mx-1">
            {format(filters.dateRange.from, "MMM dd")} — {format(filters.dateRange.to, "MMM dd")}
          </span>
        </div>

        {/* Separator */}
        <div className="w-px h-4 bg-gray-300 mx-1" />

        {/* Program Filter */}
        <div className="flex items-center gap-1">
          <span className="text-sm text-gray-500 font-medium">Chart Type:</span>
          <Select
            value={filters.program}
            onValueChange={(value) =>
              handleFilterChange("program", value)
            }
          >
            <SelectTrigger className="w-34 h-6 text-sm border-0 bg-white/60 hover:bg-white">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Charts</SelectItem>
              <SelectItem value="28">CSC</SelectItem>
              <SelectItem value="17">DLS</SelectItem>
              <SelectItem value="AIDE">AIDE</SelectItem>
              <SelectItem value="SN">SN</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="auditor-view-toggle"
            checked={filters.auditorView}
            onCheckedChange={(checked) => handleFilterChange("auditorView", checked === true)}
          />
          <label htmlFor="auditor-view-toggle" className="text-sm text-gray-500 font-medium cursor-pointer">
            Auditor View
          </label>
        </div>

        {/* Status Filter */}
        <div className="flex items-center gap-1">
          <span className="text-sm text-gray-500 font-medium">Status:</span>
          <Select
            value={filters.status}
            onValueChange={(value) =>
              handleFilterChange("status", value)
            }
          >
            <SelectTrigger className="w-24 h-6 text-sm border-0 bg-white/60 hover:bg-white">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="correction">Correction</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Type Filter */}
        {/* <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500 font-medium">Type:</span>
          <Select
            value={filters.batchType || "real-run"}
            onValueChange={(value) =>
              handleFilterChange("batchType", value)
            }
          >
            <SelectTrigger className="w-20 h-6 text-xs border-0 bg-white/60 hover:bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="real-run">Real</SelectItem>
              <SelectItem value="test-run">Test</SelectItem>
            </SelectContent>
          </Select>
        </div> */}
        {dashBoardMenu?.userPermissions?.includes("dashboard:export") && (
          <Button size="sm" className="text-sm transition transform duration-150 active:scale-95" variant="outline"
            onClick={downloadDashboardData}>
            <Share className="w-4 h-4 mr-2" />
            Export Data
          </Button>)}

        {/* Active Filters & Clear */}
        {activeFilterCount > 0 && (
          <>
            <div className="w-px h-4 bg-gray-300 mx-1" />
            <div className="flex items-center gap-1">
              <Badge
                variant="secondary"
                className="text-xs py-0 px-1.5 h-5 bg-blue-100 text-blue-700 border-blue-200"
              >
                <Filter className="w-2.5 h-2.5 mr-0.5" />
                {activeFilterCount}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="h-5 px-1.5 text-xs hover:bg-red-50 hover:text-red-600"
              >
                <X className="w-2.5 h-2.5" />
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Custom Date Picker Popover */}
      <Popover
        open={showDatePicker}

      >
        <PopoverTrigger asChild>
          <div></div>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0"
          align="start"
        >
          <div className="p-4">
            <div className="text-xs font-medium mb-2">
              Select Date Range
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-muted-foreground">
                  From
                </div>
                <Calendar
                  className="scale-90"   // shrink everything by 90%
                  classNames={{
                    day: "size-6 p-0 text-xs",      // smaller day cells
                    head_cell: "w-6 text-[10px]",   // smaller header
                    caption_label: "text-xs",       // smaller month label
                  }}
                  mode="single"
                  selected={tempRange.from}
                  onSelect={(date) => date && setTempRange((prev) => ({ ...prev, from: date }))}
                  initialFocus
                  disabled={{ after: new Date() }}
                />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">
                  To
                </div>
                <Calendar
                  className="scale-90"   // shrink everything by 90%
                  classNames={{
                    day: "size-6 p-0 text-xs",      // smaller day cells
                    head_cell: "w-6 text-[10px]",   // smaller header
                    caption_label: "text-xs",       // smaller month label
                  }}
                  mode="single"
                  selected={tempRange.to}
                  onSelect={(date) => date && setTempRange((prev) => ({ ...prev, to: date }))}
                  disabled={{ after: new Date() }}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() =>{ 
                  setShowDatePicker(false);
                  setTempRange({
                      from: filters.dateRange.from,
                      to: filters.dateRange.to,
                    })}}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  if (tempRange.from > tempRange.to) {
                    toast.error("To Date must be later than the From Date.")
                    

                    return;
                  };
                  handleFilterChange("dateRange", tempRange); setShowDatePicker(false)
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {/* <Dialog open={showDatePicker} onOpenChange={setShowDatePicker}>
  <DialogContent className="sm:max-w-[600px]">
    <DialogHeader>
      <DialogTitle>Select Custom Date Range</DialogTitle>
    </DialogHeader>
    <div className="grid grid-cols-2 gap-6 py-4">
      <div>
        <div className="text-sm font-medium mb-3 text-gray-700">
          From Date
        </div>
        <Calendar
          mode="single"
          selected={filters.dateRange.from}
          onSelect={(date) =>
            date &&
            handleFilterChange("dateRange", {
              ...filters.dateRange,
              from: startOfDay(date),
            })
          }
          initialFocus
          className="rounded-md border"
        />
      </div>
      <div>
        <div className="text-sm font-medium mb-3 text-gray-700">
          To Date
        </div>
        <Calendar
          mode="single"
          selected={filters.dateRange.to}
          onSelect={(date) =>
            date &&
            handleFilterChange("dateRange", {
              ...filters.dateRange,
              to: endOfDay(date),
            })
          }
          className="rounded-md border"
        />
      </div>
    </div>
    <div className="flex justify-between items-center pt-4 border-t">
      <div className="text-sm text-gray-600">
        Selected: {format(filters.dateRange.from, "MMM dd, yyyy")} - {format(filters.dateRange.to, "MMM dd, yyyy")}
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          onClick={() => setShowDatePicker(false)}
        >
          Cancel
        </Button>
        <Button
          onClick={() => setShowDatePicker(false)}
        >
          Apply Range
        </Button>
      </div>
    </div>
  </DialogContent>
</Dialog> */}
    </div>
  );
}